// Cash Closing service (Admin) — API_INTEGRATION.md §3.35
// History view of denomination-wise closings across locations.
// Traceability: FR-BILL-048

import { apiClient, apiClientPaginated } from '@/lib/api-client';
import type { CashClosingRecord } from '@bbpos/types';

// Re-export canonical types so existing imports
// (`from '@/services/cash-closing.service'`) keep compiling.
export type { CashClosingRecord } from '@bbpos/types';

export interface CashClosingListParams {
  page?: number;
  limit?: number;
  location_id?: string;
  start_date?: string;
  end_date?: string;
}

export interface CashClosingStatsParams {
  location_id?: string;
  start_date?: string;
  end_date?: string;
}

// Dataset-scoped stats for the selected filter range, independent of pagination.
export interface CashClosingStats {
  total: number;
  avg_variance_pct: number;
  shortfall_count: number;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function buildQuery(params: Record<string, string | number | boolean | undefined | null>): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null && v !== '',
  );
  if (entries.length === 0) return '';
  return '?' + entries.map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&');
}

// ─── Service ────────────────────────────────────────────────────────────────

export const cashClosingAdminService = {
  async list(params: CashClosingListParams = {}): Promise<{
    data: CashClosingRecord[];
    meta: { total: number; page: number; limit: number; totalPages: number };
  }> {
    const q = buildQuery({
      page: params.page,
      limit: params.limit ?? 20,
      location_id: params.location_id,
      start_date: params.start_date,
      end_date: params.end_date,
    });
    return apiClientPaginated<CashClosingRecord[]>(`/cash-closing${q}`);
  },

  // Dataset-scoped stats (Avg Variance % / shortfall count) across the whole
  // filtered range — so the stat cards stay stable across pages (issue #176).
  async stats(params: CashClosingStatsParams = {}): Promise<CashClosingStats> {
    const q = buildQuery({
      location_id: params.location_id,
      start_date: params.start_date,
      end_date: params.end_date,
    });
    return apiClient<CashClosingStats>(`/cash-closing/stats${q}`);
  },
};
